import { useAxiosRequest } from "api/axiosRequest"
import { BASE_URL } from "api/url_helper"
import axios from "axios"
import { useFormikContext } from "formik"
import { Organisation } from "modules/Authentication/store/user/interfaces"
import { useCallback, useEffect, useState } from "react"
import Spinner from "react-bootstrap/Spinner"
import { useDropzone } from "react-dropzone"
import { toast } from "react-toastify"
import { ReportFormikValues, UploadEditFields } from "./AdminReportsUploadForm"

interface AdminReportsDropZoneProps {
  uploadedFile: string
  fileName: string | undefined
  setFileName: (fileName: string) => void
  chosenOrganisation: Organisation
  setFieldTouched: (
    field: UploadEditFields,
    isTouched?: boolean | undefined,
    shouldValidate?: boolean | undefined
  ) => void
  setFieldValue: (
    field: UploadEditFields,
    value: any,
    shouldValidate?: boolean | undefined
  ) => void
}
interface UploadReportRequest {
  file: string
}

interface UploadReportResponse {
  filePath: string
}
export function AdminReportsDropZone({
  fileName,
  setFileName,
  chosenOrganisation,
  setFieldValue,
  setFieldTouched,
}: AdminReportsDropZoneProps) {
  const request = useAxiosRequest()
  const [isLoadingUploadFile, setIsLoadingUploadFile] = useState(false)

  const { values }: { values: ReportFormikValues } = useFormikContext()

  const onDrop = useCallback(
    <T extends File>(acceptedFiles: T[]): T[] => {
      acceptedFiles.forEach((file) => {
        const reader = new FileReader()

        reader.onabort = () => console.log("file reading was aborted")
        reader.onerror = () => console.log("file reading has failed")
        reader.readAsDataURL(file)
        reader.onloadend = async () => {
          let base64data = reader.result
          if (!base64data || !(typeof base64data === "string")) {
            return
          }

          base64data = base64data.replace("data:application/pdf;base64,", "")
          setIsLoadingUploadFile(true)
          try {
            const responseBody = await request<
              UploadReportRequest,
              UploadReportResponse
            >({
              url: `${BASE_URL}/reports/upload`,
              method: "post",
              data: {
                file: base64data,
              },
            })

            if (!responseBody) {
              return
            }

            if (!responseBody.data || !responseBody.data.filePath) {
              console.error("Data is null, undefined or an empty string")
            }

            setFileName(file.name)
            setFieldValue("filePath", responseBody.data.filePath)
            if (!values.reportName) {
              const reportNameFromFileName = file.name.split(".")[0]
              setFieldValue("reportName", reportNameFromFileName)
            }
          } catch (err) {
            setFieldTouched("filePath", true)
            let message = "Error uploading to server"
            if (axios.isAxiosError(err)) {
              const axiosErroMessage = err?.response?.data?.error?.message
              message = axiosErroMessage
                ? message + ": " + axiosErroMessage
                : message + "."
            } else if (err instanceof Error) {
              message = message + ": " + err.message
            } else {
              message = "."
            }
            toast.warn(message, {
              position: "bottom-right",
              autoClose: 8000,
            })
          } finally {
            setIsLoadingUploadFile(false)
          }
        }
      })

      return acceptedFiles
    },
    [setFieldValue, setFileName, setFieldTouched, chosenOrganisation.value]
  )

  const { getRootProps, getInputProps, fileRejections } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: {
      "application/pdf": [".pdf"],
    },
  })

  useEffect(() => {
    if (fileRejections.length === 0) {
      return
    }

    fileRejections.forEach((rejectedFile) => {
      const rejectedFileMessage = rejectedFile.errors[0].message
      toast.warn(rejectedFileMessage, {
        position: "bottom-right",
        autoClose: 4000,
      })
    })
  }, [fileRejections])

  return (
    <>
      <section>
        <div {...getRootProps({ className: "dropzone" })}>
          <input {...getInputProps()} />
          {isLoadingUploadFile && (
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          )}
          {values.filePath ? (
            <div className="AdminReportsDropZone_Content">
              <div className="AdminReportsDropZone_Inner">
                <div className="mb-3">
                  <i className="mdi mdi-cloud-upload display-4 text-muted"></i>
                </div>
                <h4>Drop files here or click to replace:</h4>
                <div className="mb-2" style={{ color: "#ffad00" }}>
                  {fileName ? fileName : values.filePath}
                </div>
                <em>(Only one *.pdf file will be accepted)</em>
              </div>
            </div>
          ) : (
            <div className="AdminReportsDropZone_Content">
              <div className="AdminReportsDropZone_Inner">
                <div className="mb-3">
                  <i className="mdi mdi-cloud-upload display-4 text-muted"></i>
                </div>
                <h4>Drop files here or click to upload.</h4>
                <em>(Only one *.pdf file will be accepted)</em>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  )
}
